import type { AutocompleteChangeReason } from "@mui/material";
import Autocomplete, {
  type AutocompleteProps,
} from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import type { SelectOption } from "core/entities";
import { exhaustiveCheck } from "core/utils/exhaustiveCheck";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  type PathValue,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

export interface ReactHookMultiSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends Omit<
    AutocompleteProps<
      SelectOption<PathValue<TFieldValues, TName>>,
      false,
      false,
      false
    >,
    "renderInput"
  > {
  name: TName;
  label: string;
  options: SelectOption<PathValue<TFieldValues, TName>>[];
  onChangeCallback?: (
    value: SelectOption<PathValue<TFieldValues, TName>> | null,
    reason?: AutocompleteChangeReason
  ) => void;
  placeholder?: string;
  isLoading?: boolean;
  loadingText?: string;
  required?: boolean;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  limitTags?: number;
  control?: Control<TFieldValues>;
  testId?: string;
  disableCloseOnSelect?: boolean;
  "data-testid"?: string;
}

export function ReactHookAutocomplete<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  name,
  label,
  options = [],
  placeholder,
  onChangeCallback,
  isLoading = false,
  loadingText,
  required = false,
  rules,
  limitTags = 2,
  control,
  disableCloseOnSelect = false,
  "data-testid": dataTestId = "react-hook-autocomplete",
  ...rest
}: ReactHookMultiSelectProps<TFieldValues, TName>) {
  const { t } = useTranslation("Fixhub");
  const {
    field: { onChange: fieldOnChange, ref, value: fieldValue },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      required: required
        ? t("Next:Core:FieldRequiredValidationMessage")
        : false,
      ...rules,
    },
  });
  const value = options.find((option) => option.value === fieldValue) ?? null;

  return (
    <Autocomplete
      noOptionsText={t("Next:Core:Autocomplete.noOptions")}
      data-testid={dataTestId}
      ref={ref}
      loading={isLoading}
      loadingText={loadingText}
      value={value}
      limitTags={limitTags}
      disableCloseOnSelect={disableCloseOnSelect}
      onChange={(_e, selectedOption, reason) => {
        switch (reason) {
          case "clear":
            fieldOnChange("");
            break;
          case "selectOption":
            if (selectedOption) {
              fieldOnChange(selectedOption.value);
            }
            break;
          case "blur":
          case "removeOption":
          case "createOption":
            break;
          default:
            exhaustiveCheck(reason);
        }
        if (typeof onChangeCallback === "function") {
          onChangeCallback(selectedOption, reason);
        }
      }}
      options={options}
      {...rest}
      renderInput={(params) => (
        <TextField
          error={Boolean(error)}
          helperText={error?.message}
          {...params}
          label={required ? `* ${label}` : label}
          placeholder={placeholder}
        />
      )}
    />
  );
}
